import { z } from 'zod';
import { ClickAction } from '../enums/click-action';
import {
  ClickActionSchemaMap,
  DeepLinkPayload,
  PushNotificationData,
} from '../models/push-notification';

const clickActionValues = new Set<string>(Object.values(ClickAction));

function isClickAction(value: string): value is ClickAction {
  return clickActionValues.has(value);
}

/** Parse `pinpoint.jsonBody` or API `deep_link_data` into a normalized deep-link object. */
export function parseDeepLinkRaw(raw: unknown): unknown {
  if (raw == null) {
    return null;
  }
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as unknown;
    } catch {
      return null;
    }
  }
  return raw;
}

/**
 * Accepts flat payloads (`{ click_action, job_id, ... }`) and nested
 * (`{ click_action, params: { ... } }`) shapes from Pinpoint / API.
 */
export function normalizeDeepLinkPayload(
  raw: unknown,
): { click_action: ClickAction; params: Record<string, unknown> } | null {
  const parsed = parseDeepLinkRaw(raw);
  if (!parsed || typeof parsed !== 'object') {
    return null;
  }

  const record = parsed as Record<string, unknown>;
  const clickActionRaw = record['click_action'];
  if (typeof clickActionRaw !== 'string' || !isClickAction(clickActionRaw)) {
    return null;
  }

  const nested = record['params'];
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    return {
      click_action: clickActionRaw,
      params: nested as Record<string, unknown>,
    };
  }

  const { click_action: _ignored, params: _nested, ...rest } = record;
  return {
    click_action: clickActionRaw,
    params: rest,
  };
}

const DEEP_LINK_ID_KEYS = [
  'job_id',
  'load_id',
  'quote_id',
  'payment_id',
  'vehicle_id',
  'shipper_sub',
  'carrier_id',
  'parent_sub',
  'promotion_id',
  'alert_id',
  'maintenance_id',
  'device_id',
  'driver_id',
  'shipper_id',
] as const;

/** API / Pinpoint may send numeric ids; Zod schemas expect strings. */
export function coerceDeepLinkParamIds(
  params: Record<string, unknown>,
): Record<string, unknown> {
  const out = { ...params };
  for (const key of DEEP_LINK_ID_KEYS) {
    if (out[key] != null && typeof out[key] !== 'string') {
      out[key] = String(out[key]);
    }
  }
  for (const key of ['job_number', 'load_number', 'plate_number'] as const) {
    if (out[key] != null && typeof out[key] !== 'string') {
      out[key] = String(out[key]);
    }
  }
  return out;
}

export function safeValidateDeepLinkPayload(action: ClickAction, params: unknown) {
  const schema = ClickActionSchemaMap[action];
  const normalized =
    params && typeof params === 'object' && !Array.isArray(params)
      ? coerceDeepLinkParamIds(params as Record<string, unknown>)
      : params;
  return schema.safeParse(normalized);
}

export function extractDeepLinkFromFcmData(
  data: PushNotificationData | Record<string, string | undefined>,
): unknown {
  const jsonBody = data['pinpoint.jsonBody'];
  if (jsonBody) {
    return parseDeepLinkRaw(jsonBody);
  }
  return data;
}
